import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import LegalPlaceholder from './pages/PlaceholderPage';
import LegalDashboard from './pages/LegalDashboard';
import LegalAnalytics from './pages/LegalAnalytics';
import VendorsList from '@/pages/VendorsList';
import VendorDetails from '@/pages/VendorDetails';
import CreateVendor from '@/pages/CreateVendor';
import AutoRenewalDashboard from '@/pages/AutoRenewalDashboard';
import VendorAnalytics from '@/pages/VendorAnalytics';
import ContractsList from '@/pages/ContractsList';
import ContractDetails from '@/pages/ContractDetails';
import CreateContract from '@/pages/CreateContract';
import ContractRequests from '@/pages/ContractRequests';
import MattersList from '@/pages/MattersList';
import CreateMatter from '@/pages/CreateMatter';
import MatterDetails from '@/pages/MatterDetails';
import LegalHoldsList from '@/pages/LegalHoldsList';
import LegalHoldDetails from '@/pages/LegalHoldDetails';
import CreateLegalHold from '@/pages/CreateLegalHold';
import LegalFinancialDashboard from '@/pages/LegalFinancialDashboard';

const TrackingRequestsList = lazy(() => import('@/pages/TrackingRequestsList'));
const CreateTrackingRequest = lazy(() => import('@/pages/CreateTrackingRequest'));
const TrackingRequestDetails = lazy(() => import('@/pages/TrackingRequestDetails'));
const TrackingDashboard = lazy(() => import('@/pages/TrackingDashboard'));
const HipaaIncidentsList = lazy(() => import('@/pages/HipaaIncidentsList'));
const CreateHipaaIncident = lazy(() => import('@/pages/CreateHipaaIncident'));
const HipaaIncidentDetails = lazy(() => import('@/pages/HipaaIncidentDetails'));
const HipaaDashboard = lazy(() => import('@/pages/HipaaDashboard'));
const HipaaHhsAnnualWorksheet = lazy(() => import('@/pages/HipaaHhsAnnualWorksheet'));
const BusinessEntitiesList = lazy(() => import('@/pages/BusinessEntitiesList'));
const CreateBusinessEntity = lazy(() => import('@/pages/CreateBusinessEntity'));
const BusinessEntityDetails = lazy(() => import('@/pages/BusinessEntityDetails'));
const ReferenceListAdmin = lazy(() => import('@/pages/ReferenceListAdmin'));
const CustomContractsList = lazy(() => import('@/pages/CustomContractsList'));
const CreateCustomContract = lazy(() => import('@/pages/CreateCustomContract'));
const CustomContractDetails = lazy(() => import('@/pages/CustomContractDetails'));
const CustomContractDashboard = lazy(() => import('@/pages/CustomContractDashboard'));

/**
 * VIS-151: the L&C Dashboard and Analytics are visible only to the L&C roles
 * (SUPER_ADMIN, ADMIN, LEGAL_ADMIN). Security-only roles are hidden in the nav
 * AND blocked at the route via this guard (redirect to /unauthorized). Every
 * other /legal route keeps its existing gateway-only protection.
 */
const LC_DASHBOARD_ROLES = ['SUPER_ADMIN', 'ADMIN', 'LEGAL_ADMIN'] as const;

/**
 * Legal & Compliance module route definitions.
 * Vendor, Contract, and Legal Matter pages are fully implemented.
 * Other pages remain as placeholders until their services are built.
 */
export const legalRoutes = (
  <>
    <Route index element={<LegalPlaceholder title="Legal & Compliance" description="Manage vendors, legal matters, compliance programs, and regulatory requirements." icon="&#9878;" />} />
    <Route
      path="dashboard"
      element={
        <ProtectedRoute requiredRole={[...LC_DASHBOARD_ROLES]} requireAny>
          <LegalDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="analytics"
      element={
        <ProtectedRoute requiredRole={[...LC_DASHBOARD_ROLES]} requireAny>
          <LegalAnalytics />
        </ProtectedRoute>
      }
    />

    {/* Vendor Management - fully implemented */}
    <Route path="vendors" element={<VendorsList />} />
    <Route path="vendors/create" element={<CreateVendor />} />
    <Route path="vendors/auto-renewals" element={<AutoRenewalDashboard />} />
    <Route path="vendors/analytics" element={<VendorAnalytics />} />
    <Route path="vendors/:id" element={<VendorDetails />} />
    <Route path="vendors/:id/edit" element={<CreateVendor />} />

    {/* Contract Management - fully implemented */}
    <Route path="contracts" element={<ContractsList />} />
    <Route path="contracts/create" element={<CreateContract />} />
    <Route path="contracts/:id" element={<ContractDetails />} />

    {/* Contract Request Intake */}
    <Route path="contract-requests" element={<ContractRequests />} />

    {/* Legal Matters - fully implemented */}
    <Route path="matters" element={<MattersList />} />
    <Route path="matters/create" element={<CreateMatter />} />
    <Route path="matters/:id" element={<MatterDetails />} />
    <Route path="matters/:id/edit" element={<CreateMatter />} />

    {/* Legal Holds - fully implemented */}
    <Route path="holds" element={<LegalHoldsList />} />
    <Route path="holds/create" element={<CreateLegalHold />} />
    <Route path="holds/:id" element={<LegalHoldDetails />} />

    {/* Legal Financial Dashboard - fully implemented */}
    <Route path="financial" element={<LegalFinancialDashboard />} />

    {/* Privacy & Legal Document Tracking - fully implemented */}
    <Route path="tracking" element={<TrackingRequestsList />} />
    <Route path="tracking/create" element={<CreateTrackingRequest />} />
    <Route path="tracking/dashboard" element={<TrackingDashboard />} />
    <Route path="tracking/:id" element={<TrackingRequestDetails />} />

    {/* HIPAA Incident Tracking - fully implemented */}
    <Route path="hipaa" element={<HipaaIncidentsList />} />
    <Route path="hipaa/create" element={<CreateHipaaIncident />} />
    <Route path="hipaa/dashboard" element={<HipaaDashboard />} />
    <Route path="hipaa/hhs-annual-worksheet" element={<HipaaHhsAnnualWorksheet />} />

    {/* Business Entities (VIS-129) — registered BEFORE /hipaa/:id so the
        literal segment wins over the dynamic incident id route. */}
    <Route path="hipaa/business-entities" element={<BusinessEntitiesList />} />
    <Route path="hipaa/business-entities/create" element={<CreateBusinessEntity />} />
    <Route path="hipaa/business-entities/:id" element={<BusinessEntityDetails />} />
    <Route path="hipaa/business-entities/:id/edit" element={<CreateBusinessEntity />} />

    <Route path="hipaa/:id" element={<HipaaIncidentDetails />} />

    {/* Reference List Admin */}
    <Route path="admin/reference-lists" element={<ReferenceListAdmin />} />

    {/* Custom Contract Management - fully implemented */}
    <Route path="custom-contracts" element={<CustomContractsList />} />
    <Route path="custom-contracts/create" element={<CreateCustomContract />} />
    <Route path="custom-contracts/dashboard" element={<CustomContractDashboard />} />
    <Route path="custom-contracts/:id" element={<CustomContractDetails />} />

    {/* Placeholder pages - awaiting service implementation */}
    <Route path="governance" element={<LegalPlaceholder title="Governance" description="Proxy voting, board management, and corporate governance tools." icon="&#127963;" />} />
    <Route path="compliance" element={<LegalPlaceholder title="Compliance Programs" description="Manage compliance programs, audits, and assessment tracking." icon="&#9989;" />} />
    <Route path="policies" element={<LegalPlaceholder title="Policy Management" description="Create, review, and distribute corporate policies." icon="&#128196;" />} />
    <Route path="regulatory" element={<LegalPlaceholder title="Regulatory Tracking" description="Track regulatory requirements, deadlines, and filings." icon="&#128209;" />} />
  </>
);
